import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Divider,
  TextField,
  Button,
} from '@mui/material';
import {
  SportsBasketball as BasketballIcon,
} from '@mui/icons-material';
import { playerService, User, FantasyTeam, FantasyTeamPlayer, Player, FantasyLeague, LeagueStanding, FantasyTeamGameScheduleDto } from '../services/playerService';
import { nblService } from '../services/nblService';
import { useUser } from '../contexts/UserContext';
import { usePlayer } from '../contexts/PlayerContext';
import GamePlayerDialog from '../components/GamePlayerDialog';

interface PlayerWithRoundScore {
  player: Player;
  roundScore: number;
  position?: string;
  lineupPosition?: string | null; // PG, SG, SF, PF, C, BENCH1, BENCH2, BENCH3, or null/INACTIVE
  todayScore?: number; // Score from selected date's game if available
  gameId?: number; // Game ID if player played on selected date
  gameDate?: string; // Game date if player played on selected date
  gameStatus?: string; // Game status (Live, Final, Scheduled)
  timeRemaining?: string; // Time remaining in the game
  quarter?: string; // Current quarter
}

// Helper function to determine if a player is active
const isPlayerActive = (lineupPosition: string | null | undefined): boolean => {
  return lineupPosition !== null && lineupPosition !== undefined && lineupPosition !== 'INACTIVE';
};

interface TeamMatchupData {
  team: FantasyTeam;
  ownerName: string;
  players: PlayerWithRoundScore[];
  totalRoundScore: number;
}

const MatchupPage: React.FC = () => {
  const theme = useTheme();
  const { currentUsername, isUserSwitching } = useUser();
  const { openPlayerDialog } = usePlayer();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [peroLeague, setPeroLeague] = useState<FantasyLeague | null>(null);
  const [myTeamData, setMyTeamData] = useState<TeamMatchupData | null>(null);
  const [opponentTeamData, setOpponentTeamData] = useState<TeamMatchupData | null>(null);
  const [standings, setStandings] = useState<LeagueStanding[]>([]);
  const [scheduledGames, setScheduledGames] = useState<FantasyTeamGameScheduleDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [gamePlayerDialog, setGamePlayerDialog] = useState<{
    open: boolean;
    player: Player | null;
    gameId: number | null;
  }>({ open: false, player: null, gameId: null });

  // Memoize the loadMatchupData function
  const loadMatchupData = useCallback(async (date: Date = selectedDate) => {
    if (!currentUsername) return;
    
    try {
      setLoading(true);
      setError(null);

      console.log(`🔄 MatchupPage: Loading data for user "${currentUsername}"...`);
      
      // Load user by username
      const user = await playerService.getUserByUsername(currentUsername);
      console.log('✅ MatchupPage: User loaded:', user);
      setCurrentUser(user);

      // Load user's fantasy leagues to find Pero league
      const userLeagues = await playerService.getFantasyLeaguesByUser(user.id);
      console.log('✅ MatchupPage: User leagues loaded:', userLeagues);
      
      // Find the Pero league
      const pero = userLeagues.find(league => league.name === 'Pero');
      
      if (!pero) {
        throw new Error('Pero league not found');
      }
      
      setPeroLeague(pero);
      console.log('✅ MatchupPage: Pero league found:', pero);

      // Load league standings to find opponent
      const leagueStandings = await playerService.getLeagueStandings(pero.id);
      console.log('✅ MatchupPage: League standings loaded:', leagueStandings);
      setStandings(leagueStandings);

      // Get user's fantasy teams
      const fantasyTeams = await playerService.getFantasyTeamsByUser(user.id);
      console.log('✅ MatchupPage: User fantasy teams loaded:', fantasyTeams);
      
      if (fantasyTeams.length === 0) {
        throw new Error('No fantasy teams found for user');
      }

      // Find user's team in Pero league
      const myTeam = fantasyTeams.find(team => team.leagueId === pero.id);
      if (!myTeam) {
        throw new Error('User team not found in Pero league');
      }

      // Get team players
      const myTeamPlayers = await playerService.getTeamPlayers(myTeam.id);
      console.log('✅ MatchupPage: My team players loaded:', myTeamPlayers.length);

      // Load scheduled games (round robin) for this team to find the opponent for the round
      try {
        const teamScheduledGames = await playerService.getFantasyTeamGamesByTeam(myTeam.id);
        setScheduledGames(teamScheduledGames);
        console.log('✅ MatchupPage: Scheduled games loaded:', teamScheduledGames.length);
      } catch (err) {
        console.warn('⚠️ MatchupPage: Could not load scheduled games, will fallback to standings:', err);
      }

      // Get player stats for selected date for my team
      const myPlayerIds = myTeamPlayers.map(ftp => ftp.player.id);
      const dateStats = await nblService.getPlayerStatsForDate(myPlayerIds, date);
      console.log('✅ MatchupPage: Date stats loaded for', date.toISOString().split('T')[0], ':', dateStats.length);

      // Get games for the selected date to get game IDs
      const dateGames = await nblService.getGamesByDate(date);
      console.log('✅ MatchupPage: Date games loaded:', dateGames.length);

      // Determine the round from the selected date's games (fallback to nearest round <= date)
      let selectedRound = dateGames.length > 0 ? dateGames[0].round : null;
      
      // ... (selectedDateOnly defined below) ...

      // Since getAllGames() only returns last 50 games and may not include the round we need,
      // we need to fetch games for dates around the selected date to find all games in that round
      // Rounds typically span 1-2 weeks, so fetch games for 14 days before and after
      const selectedDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      selectedDateOnly.setHours(23, 59, 59, 999); // Include the full selected date
      console.log(`🔍 MatchupPage: Selected date: ${date.toISOString()}, Selected date only: ${selectedDateOnly.toISOString()}`);
      console.log(`🔍 MatchupPage: Looking for games in round ${selectedRound}`);
      
      // Fetch games for dates around the selected date (14 days before and after)
      const dateRangeGames: typeof dateGames = [];
      const datePromises: Promise<typeof dateGames>[] = [];
      
      for (let i = -14; i <= 14; i++) {
        const checkDate = new Date(date);
        checkDate.setDate(checkDate.getDate() + i);
        datePromises.push(
          nblService.getGamesByDate(checkDate).catch(err => {
            console.debug(`Failed to get games for date ${checkDate.toISOString().split('T')[0]}:`, err);
            return [];
          })
        );
      }
      
      // Wait for all date queries to complete
      const dateGameResults = await Promise.all(datePromises);
      dateGameResults.forEach(games => dateRangeGames.push(...games));
      
      // Combine with games from selected date and deduplicate by ID
      const gamesMap = new Map<number, typeof dateGames[0]>();
      [...dateGames, ...dateRangeGames].forEach(game => {
        if (!gamesMap.has(game.id)) {
          gamesMap.set(game.id, game);
        }
      });
      
      const uniqueGames = Array.from(gamesMap.values());
      console.log(`🔍 MatchupPage: Retrieved ${uniqueGames.length} unique games from date range`);

      // Fallback: determine current round as the highest round whose gameDate is <= selected date
      if (!selectedRound) {
        const roundsUpToDate = uniqueGames
          .filter(g => g.gameDate)
          .filter(g => {
            const gameDateOnly = new Date(g.gameDate!);
            gameDateOnly.setHours(0, 0, 0, 0);
            return gameDateOnly <= selectedDateOnly;
          })
          .map(g => g.round);
        if (roundsUpToDate.length > 0) {
          selectedRound = Math.max(...roundsUpToDate);
          console.log('✅ MatchupPage: Fallback round from date-range games:', selectedRound);
        } else {
          console.warn('⚠️ MatchupPage: Could not determine round from date-range games');
        }
      } else {
        console.log('✅ MatchupPage: Selected round from date games:', selectedRound);
      }
      
      // Filter games in the selected round up to the selected date
      const roundGamesUpToDate = uniqueGames.filter(game => {
        const roundMatch = selectedRound !== null ? game.round === selectedRound : false;
        const hasDate = !!game.gameDate;
        let dateMatch = false;
        
        if (game.gameDate) {
          const gameDateOnly = new Date(game.gameDate);
          gameDateOnly.setHours(0, 0, 0, 0);
          dateMatch = gameDateOnly <= selectedDateOnly;
        }
        
        return roundMatch && hasDate && dateMatch;
      });
      
      console.log(`✅ MatchupPage: Found ${roundGamesUpToDate.length} games in round ${selectedRound} up to selected date`);
      console.log('✅ MatchupPage: Round games details:', roundGamesUpToDate.map(g => ({ 
        id: g.id, 
        date: g.gameDate, 
        round: g.round,
        status: g.status 
      })));
      
      if (roundGamesUpToDate.length === 0) {
        console.error(`❌ MatchupPage: No games found in round ${selectedRound}! This will result in 0 scores.`);
        const availableRounds = Array.from(new Set(uniqueGames.map(g => g.round)));
        console.error(`❌ MatchupPage: Available rounds in date range games:`, availableRounds);
      }

      // Build a map of playerId -> cumulative fantasy points in the round up to selected date
      const playerRoundScoreMap = new Map<number, number>(); // playerId -> cumulative round score
      
      console.log('🔍 MatchupPage: Starting aggregation for', myPlayerIds.length, 'players');
      console.log('🔍 MatchupPage: Player IDs:', myPlayerIds);
      
      // Get player stats for all games in the round up to selected date
      // Process games in parallel for better performance
      const roundGameStatsPromises = roundGamesUpToDate.map(async (game) => {
        try {
          console.log(`🔍 MatchupPage: Fetching player stats for game ${game.id} (${game.gameDate}, round ${game.round})`);
          const gamePlayerStats = await nblService.getPlayerGamesByGameId(game.id);
          console.log(`✅ MatchupPage: Got ${gamePlayerStats.length} player stats for game ${game.id} (${game.gameDate})`);
          
          // Log sample of player stats
          if (gamePlayerStats.length > 0) {
            const sampleStats = gamePlayerStats.slice(0, 3);
            console.log(`🔍 MatchupPage: Sample stats from game ${game.id}:`, sampleStats.map(s => ({
              playerId: s.playerId,
              playerName: s.playerName,
              fantasyPoints: s.fantasyPoints
            })));
          }
          
          return { gameId: game.id, gameDate: game.gameDate, stats: gamePlayerStats };
        } catch (err) {
          console.error(`❌ MatchupPage: Error fetching stats for game ${game.id}:`, err);
          return { gameId: game.id, gameDate: game.gameDate, stats: [] };
        }
      });
      
      const allRoundGameStats = await Promise.all(roundGameStatsPromises);
      console.log(`✅ MatchupPage: Retrieved stats for ${allRoundGameStats.length} games`);
      
      // Aggregate fantasy points for each player across all round games
      let totalStatsProcessed = 0;
      let totalPointsAdded = 0;
      
      allRoundGameStats.forEach(({ gameId, gameDate, stats }) => {
        console.log(`🔍 MatchupPage: Processing ${stats.length} stats from game ${gameId}`);
        stats.forEach(playerStat => {
          totalStatsProcessed++;
          if (myPlayerIds.includes(playerStat.playerId)) {
            const currentScore = playerRoundScoreMap.get(playerStat.playerId) || 0;
            const pointsToAdd = playerStat.fantasyPoints || 0;
            playerRoundScoreMap.set(playerStat.playerId, currentScore + pointsToAdd);
            totalPointsAdded += pointsToAdd;
            console.log(`✅ MatchupPage: Player ${playerStat.playerId} (${playerStat.playerName}) scored ${pointsToAdd} in game ${gameId} (${gameDate}), cumulative: ${currentScore + pointsToAdd}`);
          } else {
            console.log(`🔍 MatchupPage: Skipping player ${playerStat.playerId} (${playerStat.playerName}) - not in my team`);
          }
        });
      });
      
      console.log(`🔍 MatchupPage: Processed ${totalStatsProcessed} total player stats`);
      console.log(`🔍 MatchupPage: Added ${totalPointsAdded} total points`);
      console.log('✅ MatchupPage: Final round score map:', Array.from(playerRoundScoreMap.entries()).map(([id, score]) => ({ playerId: id, score })));
      
      // Debug: Check if any players have scores
      const playersWithScores = Array.from(playerRoundScoreMap.entries()).filter(([id, score]) => score > 0);
      console.log(`🔍 MatchupPage: ${playersWithScores.length} players have scores > 0:`, playersWithScores);

      // For each game on the selected date, get player stats to map players to game IDs
      const playerGameIdMap = new Map<number, number>(); // playerId -> gameId
      for (const game of dateGames) {
        try {
          const gamePlayerStats = await nblService.getPlayerGamesByGameId(game.id);
          gamePlayerStats.forEach(playerStat => {
            if (myPlayerIds.includes(playerStat.playerId)) {
              playerGameIdMap.set(playerStat.playerId, game.id);
            }
          });
        } catch (err) {
          console.warn(`Failed to load player stats for game ${game.id}:`, err);
        }
      }

      // Get player games to calculate round scores
      const myTeamPlayersWithScores: PlayerWithRoundScore[] = myTeamPlayers.map((ftp) => {
          // Get score for selected date if available
          const dateStat = dateStats.find(stat => stat.playerId === ftp.player.id);
          // If dateStat exists, player played (even if score is 0)
          // If dateStat is undefined, player didn't play
          const dateScore = dateStat !== undefined ? (dateStat.fantasyPoints || 0) : undefined;

          // Find the game for this player if they played
          let gameId: number | undefined = undefined;
          let gameDate: string | undefined = undefined;
          let gameStatus: string | undefined = undefined;
          let timeRemaining: string | undefined = undefined;
          let quarter: string | undefined = undefined;
          if (dateStat) {
            // Use the map to find the game ID for this player
            gameId = playerGameIdMap.get(ftp.player.id);
            if (gameId) {
              const playerGame = dateGames.find(g => g.id === gameId);
              if (playerGame) {
                gameDate = playerGame.gameDate;
                gameStatus = playerGame.status || playerGame.gameStatus;
                timeRemaining = playerGame.timeRemaining;
                quarter = playerGame.quarter;
              }
            }
          }

        // Get cumulative round score from the map
        const roundScore = playerRoundScoreMap.get(ftp.player.id) || 0;
        
        // Debug logging for specific players
        console.log(`🔍 MatchupPage: Player ${ftp.player.name} (ID: ${ftp.player.id}): roundScore=${roundScore}, dateScore=${dateScore !== undefined ? dateScore : 'undefined'}`);
        
        if (roundScore === 0 && dateScore !== undefined) {
          console.warn(`⚠️ MatchupPage: Player ${ftp.player.name} (ID: ${ftp.player.id}) has dateScore ${dateScore} but roundScore is 0. Check if game is in round ${selectedRound}.`);
          console.warn(`⚠️ MatchupPage: Player ${ftp.player.name} is in playerRoundScoreMap:`, playerRoundScoreMap.has(ftp.player.id));
        }

          return {
            player: ftp.player,
            roundScore: roundScore,
            position: ftp.lineupPosition || ftp.player.position,
            lineupPosition: ftp.lineupPosition,
            todayScore: dateScore, // Can be 0 (played but scored 0) or undefined (didn't play)
            gameId: gameId,
            gameDate: gameDate,
            gameStatus: gameStatus,
            timeRemaining: timeRemaining,
            quarter: quarter,
          };
      });

      // Separate active and inactive players
      const activePlayers = myTeamPlayersWithScores.filter(p => isPlayerActive(p.lineupPosition));
      const inactivePlayers = myTeamPlayersWithScores.filter(p => !isPlayerActive(p.lineupPosition));

      // Sort active players: those with score for selected date first
      activePlayers.sort((a, b) => {
        const aHasDate = a.todayScore !== undefined;
        const bHasDate = b.todayScore !== undefined;
        if (aHasDate && !bHasDate) return -1;
        if (!aHasDate && bHasDate) return 1;
        return 0;
      });

      // Sort inactive players: those with score for selected date first
      inactivePlayers.sort((a, b) => {
        const aHasDate = a.todayScore !== undefined;
        const bHasDate = b.todayScore !== undefined;
        if (aHasDate && !bHasDate) return -1;
        if (!aHasDate && bHasDate) return 1;
        return 0;
      });

      // Calculate cumulative round total: sum of roundScore for ACTIVE players
      console.log('🔍 MatchupPage: Calculating team total from active players');
      console.log('🔍 MatchupPage: Active players:', activePlayers.map(p => ({
        name: p.player.name,
        playerId: p.player.id,
        roundScore: p.roundScore,
        todayScore: p.todayScore,
        lineupPosition: p.lineupPosition
      })));
      
      const myTeamTotal = activePlayers.reduce((sum, p) => {
        const score = p.roundScore || 0;
        console.log(`🔍 MatchupPage: Adding ${score} from ${p.player.name} (ID: ${p.player.id}), running total: ${sum + score}`);
        return sum + score;
      }, 0);
      
      console.log(`✅ MatchupPage: Final team total: ${myTeamTotal}`);

      // Recombine with active players first
      const sortedPlayers = [...activePlayers, ...inactivePlayers];

      setMyTeamData({
        team: myTeam,
        ownerName: `${user.firstName} ${user.lastName}`,
        players: sortedPlayers,
        totalRoundScore: myTeamTotal,
      });

      // Determine opponent: prefer scheduled matchup for current round; fallback to standings
      // Determine opponent: prefer scheduled matchup for current round; fallback to standings
      let opponentTeamId: number | null = null;
      const roundNumber = selectedRound ?? undefined;
      if (roundNumber !== undefined) {
        const scheduled = scheduledGames.find(g =>
          g.fantasyTeamId === myTeam.id &&
          roundNumber >= g.roundStart &&
          roundNumber <= g.roundEnd &&
          g.opponentFantasyTeamId != null
        );
        if (scheduled?.opponentFantasyTeamId) {
          opponentTeamId = scheduled.opponentFantasyTeamId;
          console.log(`✅ MatchupPage: Using scheduled opponent ${opponentTeamId} for round ${roundNumber}`);
        }
      }

      if (!opponentTeamId) {
        const myTeamIndex = leagueStandings.findIndex(s => s.teamId === myTeam.id);
        let opponentStanding: LeagueStanding | null = null;
        if (myTeamIndex >= 0 && myTeamIndex < leagueStandings.length - 1) {
          opponentStanding = leagueStandings[myTeamIndex + 1];
        } else if (myTeamIndex > 0) {
          opponentStanding = leagueStandings[myTeamIndex - 1];
        } else if (leagueStandings.length > 1) {
          opponentStanding = leagueStandings[1];
        }

        opponentTeamId = opponentStanding?.teamId ?? null;
      }

      if (opponentTeamId) {
        // Get opponent team details
        let opponentTeam: FantasyTeam | null = fantasyTeams.find(t => t.id === opponentTeamId) || null;
        if (!opponentTeam) {
          try {
            opponentTeam = await playerService.getFantasyTeam(opponentTeamId);
          } catch (err) {
            console.warn('Could not fetch opponent team from API, using fallback:', err);
          }
        }

        const opponentOwnerName = standings.find(s => s.teamId === opponentTeamId)?.ownerName || 'Unknown';

        const opponentTeamObj: FantasyTeam = opponentTeam || {
          id: opponentTeamId,
          name: 'Opponent',
          ownerId: 0,
          leagueId: pero.id,
          isActive: true,
          createdDate: new Date().toISOString(),
          logoUrl: undefined,
          teamColors: undefined,
          owner: {
            id: 0,
            username: opponentOwnerName,
            email: '',
            firstName: opponentOwnerName.split(' ')[0] || 'Unknown',
            lastName: opponentOwnerName.split(' ')[1] || '',
            dateOfBirth: new Date().toISOString(),
            createdDate: new Date().toISOString(),
            lastLoginDate: new Date().toISOString(),
            isActive: true,
            emailVerified: false,
          },
          league: pero,
          players: [],
          games: [],
          draftPicks: [],
        };

        // Try to get opponent team players, or use placeholder
        let opponentPlayers: FantasyTeamPlayer[] = [];
        try {
          opponentPlayers = await playerService.getTeamPlayers(opponentTeamObj.id);
        } catch (err) {
          console.warn('Could not load opponent players, using placeholder data:', err);
          // Use placeholder players if we can't fetch
          opponentPlayers = [];
        }

        // Get player stats for selected date for opponent team
        const opponentPlayerIds = opponentPlayers.map(ftp => ftp.player.id);
        let opponentDateStats: typeof dateStats = [];
        try {
          opponentDateStats = await nblService.getPlayerStatsForDate(opponentPlayerIds, date);
        } catch (err) {
          console.warn('Could not load opponent date stats:', err);
        }
        
        // Build opponent player round score map (reuse the round games data)
        const opponentPlayerRoundScoreMap = new Map<number, number>(); // playerId -> cumulative round score
        
        // Aggregate fantasy points for opponent players across all round games
        allRoundGameStats.forEach(({ gameId, gameDate, stats }) => {
          stats.forEach(playerStat => {
            if (opponentPlayerIds.includes(playerStat.playerId)) {
              const currentScore = opponentPlayerRoundScoreMap.get(playerStat.playerId) || 0;
              const pointsToAdd = playerStat.fantasyPoints || 0;
              opponentPlayerRoundScoreMap.set(playerStat.playerId, currentScore + pointsToAdd);
            }
          });
        });
        
        // Build opponent player to game ID map
        const opponentPlayerGameIdMap = new Map<number, number>(); // playerId -> gameId
        for (const game of dateGames) {
          try {
            const gamePlayerStats = await nblService.getPlayerGamesByGameId(game.id);
            gamePlayerStats.forEach(playerStat => {
              if (opponentPlayerIds.includes(playerStat.playerId)) {
                opponentPlayerGameIdMap.set(playerStat.playerId, game.id);
              }
            });
          } catch (err) {
            console.warn(`Failed to load player stats for game ${game.id}:`, err);
          }
        }

        // Calculate round scores for opponent team
        const opponentPlayersWithScores: PlayerWithRoundScore[] = opponentPlayers.length > 0
          ? opponentPlayers.map((ftp) => {
                // Get score for selected date if available
                const dateStat = opponentDateStats.find(stat => stat.playerId === ftp.player.id);
                // If dateStat exists, player played (even if score is 0)
                // If dateStat is undefined, player didn't play
                const dateScore = dateStat !== undefined ? (dateStat.fantasyPoints || 0) : undefined;

                // Find the game for this player if they played
                let gameId: number | undefined = undefined;
                let gameDate: string | undefined = undefined;
                let gameStatus: string | undefined = undefined;
                let timeRemaining: string | undefined = undefined;
                let quarter: string | undefined = undefined;
                if (dateStat) {
                  // Use the map to find the game ID for this player
                  gameId = opponentPlayerGameIdMap.get(ftp.player.id);
                  if (gameId) {
                    const playerGame = dateGames.find(g => g.id === gameId);
                    if (playerGame) {
                      gameDate = playerGame.gameDate;
                      gameStatus = playerGame.status || playerGame.gameStatus;
                      timeRemaining = playerGame.timeRemaining;
                      quarter = playerGame.quarter;
                    }
                  }
                }

              // Get cumulative round score from the map
              const roundScore = opponentPlayerRoundScoreMap.get(ftp.player.id) || 0;

                return {
                  player: ftp.player,
                  roundScore: roundScore,
                  position: ftp.lineupPosition || ftp.player.position,
                  lineupPosition: ftp.lineupPosition,
                  todayScore: dateScore, // Can be 0 (played but scored 0) or undefined (didn't play)
                  gameId: gameId,
                  gameDate: gameDate,
                  gameStatus: gameStatus,
                  timeRemaining: timeRemaining,
                  quarter: quarter,
                };
              })
          : []; // Empty if we couldn't load players

        // Separate active and inactive players for opponent
        const opponentActivePlayers = opponentPlayersWithScores.filter(p => isPlayerActive(p.lineupPosition));
        const opponentInactivePlayers = opponentPlayersWithScores.filter(p => !isPlayerActive(p.lineupPosition));

        // Sort opponent active players: those with score for selected date first
        opponentActivePlayers.sort((a, b) => {
          const aHasDate = a.todayScore !== undefined;
          const bHasDate = b.todayScore !== undefined;
          if (aHasDate && !bHasDate) return -1;
          if (!aHasDate && bHasDate) return 1;
          return 0;
        });

        // Sort opponent inactive players: those with score for selected date first
        opponentInactivePlayers.sort((a, b) => {
          const aHasDate = a.todayScore !== undefined;
          const bHasDate = b.todayScore !== undefined;
          if (aHasDate && !bHasDate) return -1;
          if (!aHasDate && bHasDate) return 1;
          return 0;
        });

        // Calculate cumulative round total: sum of roundScore for ACTIVE players
        const opponentTotal = opponentActivePlayers.reduce((sum, p) => {
          return sum + (p.roundScore || 0);
        }, 0);

        // Recombine with active players first
        const sortedOpponentPlayers = [...opponentActivePlayers, ...opponentInactivePlayers];

        setOpponentTeamData({
          team: opponentTeamObj,
          ownerName: standings.find(s => s.teamId === opponentTeamObj.id)?.ownerName || 'Unknown',
          players: sortedOpponentPlayers,
          totalRoundScore: opponentTotal,
        });
      }

    } catch (err) {
      console.error('❌ MatchupPage: Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load matchup data');
    } finally {
      setLoading(false);
    }
  }, [currentUsername]);

  // Handle date change
  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = new Date(event.target.value);
    setSelectedDate(newDate);
    loadMatchupData(newDate);
  };

  // Load data when component mounts or when user changes
  useEffect(() => {
    if (currentUsername && !isUserSwitching) {
      loadMatchupData(selectedDate);
    }
  }, [currentUsername, isUserSwitching, selectedDate]);

  // Show loading state while user is switching
  if (isUserSwitching) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Switching to {currentUsername}...
        </Typography>
      </Box>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading matchup...
        </Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!myTeamData || !opponentTeamData) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Alert severity="info">
          Matchup data not available. Please ensure you have a team in the Pero league.
        </Alert>
      </Box>
    );
  }

  const getTeamInitials = (teamName: string) => {
    const words = teamName.split(' ');
    if (words.length >= 2) {
      return `${words[0][0]}${words[1][0]}`.toUpperCase();
    }
    return teamName.substring(0, 2).toUpperCase();
  };

  const getPlayerInitials = (playerName: string) => {
    const parts = playerName.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    }
    return playerName.substring(0, 2).toUpperCase();
  };

  // Helper function to format time remaining
  const formatTimeRemaining = (timeRemaining?: string): string | null => {
    if (!timeRemaining) return null;
    
    // Handle ISO 8601 duration format (PT0S, PT5M30S, etc.)
    if (timeRemaining.startsWith('PT')) {
      const match = timeRemaining.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
      if (match) {
        const hours = parseInt(match[1] || '0');
        const minutes = parseInt(match[2] || '0');
        const seconds = parseInt(match[3] || '0');
        
        if (hours > 0) {
          return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        } else if (minutes > 0 || seconds > 0) {
          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
      }
    }
    
    // If it's already in a readable format, return as is
    return timeRemaining;
  };

  // Separate active and inactive players
  const myActivePlayers = myTeamData.players.filter(p => isPlayerActive(p.lineupPosition));
  const myInactivePlayers = myTeamData.players.filter(p => !isPlayerActive(p.lineupPosition));
  const opponentActivePlayers = opponentTeamData.players.filter(p => isPlayerActive(p.lineupPosition));
  const opponentInactivePlayers = opponentTeamData.players.filter(p => !isPlayerActive(p.lineupPosition));

  // Combine active players for side-by-side display
  const maxActivePlayers = Math.max(myActivePlayers.length, opponentActivePlayers.length);
  const activePlayerRows = Array.from({ length: maxActivePlayers }, (_, i) => ({
    myPlayer: myActivePlayers[i] || null,
    opponentPlayer: opponentActivePlayers[i] || null,
  }));

  // Combine inactive players for side-by-side display
  const maxInactivePlayers = Math.max(myInactivePlayers.length, opponentInactivePlayers.length);
  const inactivePlayerRows = Array.from({ length: maxInactivePlayers }, (_, i) => ({
    myPlayer: myInactivePlayers[i] || null,
    opponentPlayer: opponentInactivePlayers[i] || null,
  }));

  const leadingTeam = myTeamData.totalRoundScore >= opponentTeamData.totalRoundScore ? 'my' : 'opponent';
  const scoreDifference = Math.abs(myTeamData.totalRoundScore - opponentTeamData.totalRoundScore);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Date Selector */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 'medium' }}>
              View Matchup for:
            </Typography>
            <TextField
              type="date"
              value={selectedDate.toISOString().split('T')[0]}
              onChange={handleDateChange}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      {/* Matchup Header */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 2, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)` }}>
        <CardContent sx={{ p: 3, textAlign: 'center', color: 'white' }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
            <BasketballIcon sx={{ fontSize: 40 }} />
            Week Matchup
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            {peroLeague?.name || 'Pero'} League
          </Typography>
          
          {/* Team Scores Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, mt: 2, px: 2 }}>
            <Box sx={{ flex: 1, textAlign: 'center', maxWidth: 'calc(50% - 60px)' }}>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>Your Team</Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {myTeamData.totalRoundScore}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {myTeamData.team.name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                {myTeamData.ownerName}
              </Typography>
            </Box>
            
            <Box sx={{ flexShrink: 0, minWidth: 80, textAlign: 'center' }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold' }}>VS</Typography>
            </Box>
            
            <Box sx={{ flex: 1, textAlign: 'center', maxWidth: 'calc(50% - 60px)' }}>
              <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>Opponent</Typography>
              <Typography variant="h3" sx={{ fontWeight: 'bold', mb: 1 }}>
                {opponentTeamData.totalRoundScore}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {opponentTeamData.team.name}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
                {opponentTeamData.ownerName}
              </Typography>
            </Box>
          </Box>

          {/* Score difference indicator */}
          {scoreDifference > 0 && (
            <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.2)' }}>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {leadingTeam === 'my' ? 'You are leading' : 'Opponent is leading'} by{' '}
                <strong>{scoreDifference.toFixed(1)}</strong> points
          </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Active Players Comparison Table */}
      <Card sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden', mb: 2 }}>
        <Box sx={{ bgcolor: 'success.light', p: 1.5, borderBottom: '2px solid', borderColor: 'success.main' }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white', textAlign: 'center' }}>
            Active Players
          </Typography>
        </Box>
        <TableContainer component={Paper} elevation={0}>
          <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
            <TableHead>
              <TableRow sx={{ bgcolor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold', width: '38%' }}>Your Team</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%' }}>Score</TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '8%' }}></TableCell>
                <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%' }}>Score</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '38%', textAlign: 'right' }}>Opponent</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activePlayerRows.map((row, index) => {
                const myPlayer = row.myPlayer;
                const opponentPlayer = row.opponentPlayer;
                
                return (
                  <TableRow
                    key={index}
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      bgcolor: index % 2 === 0 ? 'inherit' : 'action.selected',
                    }}
                  >
                    {/* Your Team Player */}
                    <TableCell sx={{ verticalAlign: 'middle' }}>
                      {myPlayer ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              bgcolor: 'primary.main',
                              width: 40,
                              height: 40,
                              fontSize: '0.875rem',
                              flexShrink: 0,
                            }}
                          >
                            {getPlayerInitials(myPlayer.player.name)}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 'medium',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline', color: 'primary.main' }
                              }}
                              onClick={() => openPlayerDialog(myPlayer.player)}
                            >
                              {myPlayer.player.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {myPlayer.position || myPlayer.player.position} • {myPlayer.player.team}
          </Typography>
                          </Box>
            </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          -
                        </Typography>
                      )}
                    </TableCell>

                    {/* Your Team Score */}
                    <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                      {myPlayer ? (
                        myPlayer.todayScore !== undefined ? (
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 'bold',
                                color: 'primary.main',
                                cursor: myPlayer.gameId ? 'pointer' : 'default',
                                '&:hover': myPlayer.gameId ? { textDecoration: 'underline' } : {},
                              }}
                              onClick={() => {
                                if (myPlayer.gameId) {
                                  setGamePlayerDialog({
                                    open: true,
                                    player: myPlayer.player,
                                    gameId: myPlayer.gameId,
                                  });
                                }
                              }}
                            >
                              {myPlayer.todayScore.toFixed(1)}
                            </Typography>
                            {myPlayer.gameStatus === 'Live' && myPlayer.timeRemaining && (
                              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold', display: 'block', mt: 0.5 }}>
                                {formatTimeRemaining(myPlayer.timeRemaining) || myPlayer.timeRemaining}
                                {myPlayer.quarter && ` • ${myPlayer.quarter}`}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    {/* VS separator */}
                    <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>
                        VS
                      </Typography>
                    </TableCell>

                    {/* Opponent Score */}
                    <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                      {opponentPlayer ? (
                        opponentPlayer.todayScore !== undefined ? (
                          <Box>
                            <Typography
                              variant="body1"
                              sx={{
                                fontWeight: 'bold',
                                color: 'warning.main',
                                cursor: opponentPlayer.gameId ? 'pointer' : 'default',
                                '&:hover': opponentPlayer.gameId ? { textDecoration: 'underline' } : {},
                              }}
                              onClick={() => {
                                if (opponentPlayer.gameId) {
                                  setGamePlayerDialog({
                                    open: true,
                                    player: opponentPlayer.player,
                                    gameId: opponentPlayer.gameId,
                                  });
                                }
                              }}
                            >
                              {opponentPlayer.todayScore.toFixed(1)}
                            </Typography>
                            {opponentPlayer.gameStatus === 'Live' && opponentPlayer.timeRemaining && (
                              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold', display: 'block', mt: 0.5 }}>
                                {formatTimeRemaining(opponentPlayer.timeRemaining) || opponentPlayer.timeRemaining}
                                {opponentPlayer.quarter && ` • ${opponentPlayer.quarter}`}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            -
                          </Typography>
                        )
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          -
                        </Typography>
                      )}
                    </TableCell>

                    {/* Opponent Player */}
                    <TableCell sx={{ textAlign: 'right', verticalAlign: 'middle' }}>
                      {opponentPlayer ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row-reverse', justifyContent: 'flex-start' }}>
                          <Avatar
                            sx={{
                              bgcolor: 'secondary.main',
                              width: 40,
                              height: 40,
                              fontSize: '0.875rem',
                              flexShrink: 0,
                            }}
                          >
                            {getPlayerInitials(opponentPlayer.player.name)}
                          </Avatar>
                          <Box sx={{ textAlign: 'right', flex: 1 }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: 'medium',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline', color: 'primary.main' }
                              }}
                              onClick={() => openPlayerDialog(opponentPlayer.player)}
                            >
                              {opponentPlayer.player.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {opponentPlayer.position || opponentPlayer.player.position} • {opponentPlayer.player.team}
                            </Typography>
                          </Box>
          </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'right' }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {/* Daily Totals Summary */}
        <Box sx={{ p: 2, bgcolor: 'action.hover', borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Today's Points
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {myActivePlayers.reduce((sum, p) => sum + (p.todayScore !== undefined ? p.todayScore : 0), 0).toFixed(1)}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Today's Points
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {opponentActivePlayers.reduce((sum, p) => sum + (p.todayScore !== undefined ? p.todayScore : 0), 0).toFixed(1)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Card>

      {/* Inactive Players Comparison Table */}
      {(myInactivePlayers.length > 0 || opponentInactivePlayers.length > 0) && (
        <Card sx={{ borderRadius: 2, boxShadow: 2, overflow: 'hidden', mt: 2 }}>
          <Box sx={{ bgcolor: 'grey.300', p: 1.5, borderBottom: '2px solid', borderColor: 'grey.400' }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'text.secondary', textAlign: 'center' }}>
              Inactive Players
            </Typography>
          </Box>
          <TableContainer component={Paper} elevation={0}>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 'bold', width: '38%' }}>Your Team</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%' }}>Score</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: '8%' }}></TableCell>
                  <TableCell align="center" sx={{ fontWeight: 'bold', width: '12%' }}>Score</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '38%', textAlign: 'right' }}>Opponent</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inactivePlayerRows.map((row, index) => {
                  const myPlayer = row.myPlayer;
                  const opponentPlayer = row.opponentPlayer;
                  
                  return (
                    <TableRow
                      key={`inactive-${index}`}
                      sx={{
                        '&:hover': { bgcolor: 'action.hover' },
                        bgcolor: index % 2 === 0 ? 'inherit' : 'action.selected',
                        opacity: 0.7,
                      }}
                    >
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {myPlayer ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar sx={{ bgcolor: 'grey.400', width: 40, height: 40, fontSize: '0.875rem', flexShrink: 0 }}>
                              {getPlayerInitials(myPlayer.player.name)}
                            </Avatar>
                            <Box sx={{ flex: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'medium', cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: 'primary.main' } }} onClick={() => openPlayerDialog(myPlayer.player)}>
                                {myPlayer.player.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {myPlayer.position || myPlayer.player.position} • {myPlayer.player.team}
                              </Typography>
                            </Box>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                        {myPlayer?.todayScore !== undefined ? (
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary', cursor: myPlayer.gameId ? 'pointer' : 'default', '&:hover': myPlayer.gameId ? { textDecoration: 'underline' } : {} }} onClick={() => myPlayer.gameId && setGamePlayerDialog({ open: true, player: myPlayer.player, gameId: myPlayer.gameId })}>
                              {myPlayer.todayScore.toFixed(1)}
                            </Typography>
                            {myPlayer.gameStatus === 'Live' && myPlayer.timeRemaining && (
                              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold', display: 'block', mt: 0.5 }}>
                                {formatTimeRemaining(myPlayer.timeRemaining) || myPlayer.timeRemaining}
                                {myPlayer.quarter && ` • ${myPlayer.quarter}`}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 'medium' }}>VS</Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ verticalAlign: 'middle' }}>
                        {opponentPlayer?.todayScore !== undefined ? (
                          <Box>
                            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'text.secondary', cursor: opponentPlayer.gameId ? 'pointer' : 'default', '&:hover': opponentPlayer.gameId ? { textDecoration: 'underline' } : {} }} onClick={() => opponentPlayer.gameId && setGamePlayerDialog({ open: true, player: opponentPlayer.player, gameId: opponentPlayer.gameId })}>
                              {opponentPlayer.todayScore.toFixed(1)}
                            </Typography>
                            {opponentPlayer.gameStatus === 'Live' && opponentPlayer.timeRemaining && (
                              <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 'bold', display: 'block', mt: 0.5 }}>
                                {formatTimeRemaining(opponentPlayer.timeRemaining) || opponentPlayer.timeRemaining}
                                {opponentPlayer.quarter && ` • ${opponentPlayer.quarter}`}
                              </Typography>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'middle', textAlign: 'right' }}>
                        {opponentPlayer ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                            <Box sx={{ textAlign: 'right', flex: 1 }}>
                              <Typography variant="body1" sx={{ fontWeight: 'medium', cursor: 'pointer', '&:hover': { textDecoration: 'underline', color: 'primary.main' } }} onClick={() => openPlayerDialog(opponentPlayer.player)}>
                                {opponentPlayer.player.name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {opponentPlayer.position || opponentPlayer.player.position} • {opponentPlayer.player.team}
                              </Typography>
                            </Box>
                            <Avatar sx={{ bgcolor: 'grey.400', width: 40, height: 40, fontSize: '0.875rem', flexShrink: 0 }}>
                              {getPlayerInitials(opponentPlayer.player.name)}
                            </Avatar>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>-</Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Game-Specific Player Dialog */}
      <GamePlayerDialog
        open={gamePlayerDialog.open}
        player={gamePlayerDialog.player}
        gameId={gamePlayerDialog.gameId}
        onClose={() => setGamePlayerDialog({ open: false, player: null, gameId: null })}
      />

    </Box>
  );
};

export default MatchupPage;
